/**
 * One MeetingManager, many calls.
 *
 * This module exists because of a lifecycle change that came in with the in-app call surface
 * and was never declared. The popup used to render VideoCallContent in a SEPARATE React root
 * with its own `<MeetingProvider>`, so every call got a brand new MeetingManager and the
 * previous one died with the window. In-app, CallStage renders under the app-root
 * MeetingProvider (main.tsx -> GlobalVideoCall), so ONE MeetingManager is now shared by every
 * call for the whole browser session. Two calls in a row is a normal demo path, and it is the
 * path that never existed before.
 *
 * What the shared manager actually does between calls
 * ---------------------------------------------------
 * `MeetingManager.leave()` (component-library 3.12) is the only thing that cleans up, and it
 * cleans up properly: it stops the content share and the local video tile, destroys the device
 * controller - which is what releases the microphone and camera - unsubscribes the active
 * speaker detector, stops the audio-video facade, and then calls `initializeMeetingManager()`
 * to null the session and empty the device lists. It finishes with `publishAudioVideo()`,
 * which pushes `audioVideo = null` into the React tree, and THAT is what resets the rest:
 * RosterProvider clears the roster in its cleanup, RemoteVideoTileProvider dispatches RESET,
 * ContentShareProvider dispatches RESET, LocalVideoProvider sets isVideoEnabled false.
 *
 * So the roster, the tiles, the share state and the devices are all clean for the next call -
 * on the one condition that the leave for call N actually completes before the join for call
 * N+1 begins. Nothing enforced that. Two things could break it:
 *
 *  1. A LEAVE STILL IN FLIGHT WHEN THE NEXT CALL JOINS. `leave()` awaits
 *     `deviceController.destroy()`, and it ends with `initializeMeetingManager()` - which
 *     nulls `meetingSession` and `audioVideo` and republishes null - WITHOUT re-checking that
 *     the session it is nulling is still the one it left. Answer a second call while the first
 *     call's leave is still settling and that trailing leave demolishes the new session:
 *     no media, no roster, no tiles, on a call that has already been answered. Every teardown
 *     path fires a leave (endCall, leaveCall, the socket 'call-ended' and 'call-rejected'
 *     handlers) AND VideoCallContent fires another on unmount, so several are usually in
 *     flight at once.
 *
 *  2. A STALE UNMOUNT LEAVE. VideoCallContent's unmount cleanup is fire-and-forget and carries
 *     no idea which call it belonged to, so call N's unmount could tear down call N+1.
 *
 * Both are fixed here, in one place, by giving the session an owner and a queue:
 *
 *  - Every join and every leave runs through `serialize`, so they can never interleave and a
 *    leave is always finished before the next join starts.
 *  - The session is owned by a token (the callId). A release whose token is not the current
 *    owner is a no-op, so a late teardown from a finished call cannot touch a live one.
 *  - Ownership is per MeetingManager, because there are genuinely two in the app: the app-root
 *    one, and GuestJoinPage's own (it mounts its own MeetingProvider inside the app tree).
 *
 * The other half of "genuinely clean" is not here: VideoCallProvider keys CallStage on the
 * callId so that a second call gets a fresh VideoCallContent rather than a reused one holding
 * call N's isJoined, duration, raised hands and error.
 */

/** Serialises every join and leave. Chained on settlement, never on success. */
let queue = Promise.resolve();

/** `{ manager, token }` for the session that is up, or null. */
let session = null;

function ignore() {}

function serialize(task) {
    const run = queue.then(task, task);
    // The queue must survive a failed task, so it chains on the settled result, not this one.
    queue = run.then(ignore, ignore);
    return run;
}

/**
 * `leave()` is best-effort by design: it is called on paths where the session may already be
 * gone, and a failure to leave must never become the error the engineer sees instead of the
 * real one.
 */
async function leaveQuietly(meetingManager) {
    try {
        await meetingManager.leave();
    } catch (error) {
        console.warn('[VideoCall] Leaving the meeting session failed:', error);
    }
}

/**
 * Join and start the meeting for `token`, after making sure any previous session is fully
 * gone. Rejects with whatever `join`/`start` threw, having cleaned up first.
 */
export function startCallSession(meetingManager, token, configuration) {
    return serialize(async () => {
        if (session) {
            await leaveQuietly(session.manager);
            session = null;
        }

        // Ownership is taken BEFORE the join, not after it. A join that throws part way
        // through has still constructed a device controller and may already hold the
        // microphone, and the release path is the only thing that gives those back.
        session = { manager: meetingManager, token };

        try {
            await meetingManager.join(configuration);
            await meetingManager.start();
        } catch (error) {
            await leaveQuietly(meetingManager);
            if (session && session.manager === meetingManager && session.token === token) {
                session = null;
            }
            throw error;
        }
    });
}

/**
 * Leave the meeting - but only if `token` still owns it.
 *
 * A null or mismatched token is deliberately a no-op rather than an unconditional leave: this
 * is called from unmount cleanups and from socket teardowns that can land long after their
 * call is over, and "leave whatever is there" is exactly how one of those ends a call that
 * somebody else has since answered.
 */
export function endCallSession(meetingManager, token) {
    return serialize(async () => {
        if (token == null) return;
        if (!session || session.manager !== meetingManager || session.token !== token) return;
        session = null;
        await leaveQuietly(meetingManager);
    });
}

/** Whether `token` currently owns the session. Test/diagnostic use. */
export function ownsCallSession(meetingManager, token) {
    return !!session && session.manager === meetingManager && session.token === token;
}
